import {
  ref,
  computed,
  inject,
  provide,
  watchEffect,
  type InjectionKey,
} from "vue";
import type Ably from "ably";
import {
  useAblyChannelRegistry,
  CHANNELS,
} from "@/composables/ably/ably-channel-registry";
import { rewindChannel } from "@/composables/ably/ably.types";
import { isRecentAvatarsDelta } from "@f3/shared";
import type { RecentAvatarEntry, RecentAvatarsDelta } from "@f3/shared";
import { useChannelStore } from "@/stores/channel.store";

const recentAvatarsInjectionKey = Symbol("recentAvatars") as InjectionKey<
  ReturnType<typeof useProvideRecentAvatars>
>;

const EMPTY_DELTA: RecentAvatarsDelta = {
  type: "recent-avatars",
  epoch: -1,
  entityId: "",
  streamId: "",
  commitId: "",
  commandId: "",
  channelId: "",
  recentAvatars: [],
};

export function useProvideRecentAvatars() {
  const channelStore = useChannelStore();
  const registry = useAblyChannelRegistry();
  const recentAvatars = ref<RecentAvatarEntry[]>([]);
  const isLoaded = ref(false);

  const channelId = computed(() => channelStore.channel?.id ?? "");

  function applyDelta(data: unknown, lastApplied: Map<string, number>) {
    if (!isRecentAvatarsDelta(data)) return;

    const lastEpoch = lastApplied.get(data.streamId) ?? -Infinity;
    if (data.epoch <= lastEpoch) return;

    lastApplied.set(data.streamId, data.epoch);
    recentAvatars.value = data.recentAvatars.slice(0, 5);
    isLoaded.value = true;
  }

  watchEffect((onCleanup) => {
    const currentChannelId = channelId.value;
    recentAvatars.value = EMPTY_DELTA.recentAvatars;
    isLoaded.value = false;

    if (!currentChannelId) return;

    const lastApplied = new Map<string, number>();
    const handle = registry.subscribeChannel({
      descriptor: CHANNELS.channelState,
      channelArgs: [currentChannelId],
    });

    const listener = (msg: Ably.Message) => {
      applyDelta(msg.data, lastApplied);
    };
    const unsubscribe = handle.subscribe("state", listener);

    const stopRewind = watchEffect(() => {
      if (!handle.isAttached.value) return;
      void rewindChannel(handle, 1, listener);
    });

    onCleanup(() => {
      stopRewind();
      unsubscribe();
      handle.release();
    });
  });

  const definition = { recentAvatars, isLoaded };
  provide(recentAvatarsInjectionKey, definition);
  return definition;
}

export function useRecentAvatars() {
  const value = inject(recentAvatarsInjectionKey);
  if (!value) {
    throw new Error(
      "useRecentAvatars() is called without providing the context",
    );
  }
  return value;
}
